/**
 * Fab lighting presets.
 *
 * One choice, applied to the whole hall: the colour the room is bathed in.
 * Three moods, each of them a real thing a fab looks like rather than a theme:
 *
 *   - BRIGHT — the working cleanroom: high-key, slightly cool, hazy with
 *     distance. The default, tuned against reference photography of production
 *     bays: bright FFU panels overhead, their glare streaking on a polished
 *     floor.
 *   - BLUE — the night-shift look: the same hall lit low and cold, the way an
 *     automated bay photographs when the people are gone and only the tools
 *     are awake.
 *   - YELLOW — the litho bay's amber safelight over everything. This one is
 *     physics, not mood: resist is exposed by UV and blue, so a litho bay
 *     filters them out, and standing in that colour IS the lesson. The litho
 *     bay itself is lit this way in every preset.
 *
 * The preset is a table of colours and intensities; `FabScene.applyLighting`
 * is the only reader. Kept as data so a new mood is a row, not code.
 *
 * App-level, like language: remembered per machine, chosen from the main
 * menu, and never part of a session or a journal - the physics is identical
 * under every sky.
 */

export type LightingId = 'bright' | 'blue' | 'yellow'

export interface LightingPreset {
  id: LightingId
  /** i18n keys - resolved by the menu that shows them, not here. */
  labelKey: string
  noteKey: string
  /** Room key: what the void and the distance haze are painted. */
  background: number
  fog: number
  /** Hemisphere fill: sky (ceiling) and ground (floor bounce). */
  hemiSky: number
  hemiGround: number
  hemiIntensity: number
  /** Directional key light. */
  key: number
  keyIntensity: number
  /** Per-bay downlights (the litho bay keeps its own amber in every preset). */
  lamp: number
  lampIntensity: number
  /** The FFU ceiling's residual glow, between the fixtures. */
  ffuEmissive: number
  ffuIntensity: number
  /** The luminaire array itself - unlit panels, so this IS their brightness. */
  panel: number
  /** Tint multiplied into the polished floor. */
  floor: number
  /**
   * Environment-map contribution. The environment is a capture of the hall
   * itself, so it already carries the preset's colour; this scales how much
   * of it metals and the floor gloss pick up.
   *
   * IT IS ALSO A LIGHT, and for a long time it was the BIGGEST one in the
   * building. Measured by removal at the old value of 1.0, the environment
   * moved the hall's median pixel further than all eighteen point lights
   * put together - and it is a capture of the hall applied back onto the
   * hall, so the room was lighting itself in a loop. Worse, it arrives from
   * every direction at once, which is precisely the light that destroys
   * form: no direction means no gradient across a surface, and nothing for
   * ambient occlusion to darken.
   *
   * So it is now a reflection budget rather than a fill light, and the
   * materials that actually want it - the polished floor, the metals -
   * carry their own `envMapIntensity` to take a bigger share of a smaller
   * pot (see `createMaterials`). Dropping it from 1.0 to 0.35 widened the
   * hall's tonal range by about a third with no loss at the top end: the
   * luminaires still peg at 244, the midtones came down to meet them.
   */
  envIntensity: number
}

export const LIGHTING_PRESETS: LightingPreset[] = [
  {
    id: 'bright',
    labelKey: 'menu.LightBright',
    noteKey: 'menu.LightBrightNote',
    background: 0xd0e0ee,
    // A shade off paper white, so the far slot recedes instead of washing.
    // Not darker than the walls - a dark fog on a bright room reads as
    // smoke, and smoke is the one thing a cleanroom does not have.
    fog: 0xc9dbea,
    hemiSky: 0xe8f3ff,
    hemiGround: 0x9cadbd,
    hemiIntensity: 0.8,
    key: 0xddedfb,
    keyIntensity: 0.46,
    lamp: 0xdbeeff,
    lampIntensity: 14,
    ffuEmissive: 0xe2f1ff,
    ffuIntensity: 0.55,
    panel: 0xf2faff,
    // NEUTRAL. A fab floor is grey vinyl - check any photograph of a
    // production bay: the floor is a light neutral grey, and what makes it
    // look cool is the light falling on it and the tools reflected in it,
    // not pigment. This was 0xb6d8ea, which is painted blue, and paint
    // cannot respond to anything: it stayed blue under every light in the
    // building. Same confusion as the walls at 0.88 albedo, the tool skins
    // at 1.0 and the filter ceiling at 1.0 - colour that belongs to the
    // lighting, baked into the surface instead.
    //
    // AND DARKER, which is a different correction from that one and must not
    // be confused with it: the hue stays exactly where it is, the VALUE comes
    // down. At 0xd6dade, times the tile texture, the floor reflected about
    // 0.56 of the light landing on it and the wall reflected 0.74 - a third
    // of a stop between them. In a real bay it is nearer a full stop: white
    // coated wall panel is 0.75 to 0.85, grey vinyl tile is 0.35 to 0.45, and
    // that step is the largest single difference in value anywhere in the
    // room. Without it the floor, the walls, the tool skins and the filter
    // ceiling all sat inside about five per cent of each other and the hall
    // had no vertical gradient at all - which is most of what was left making
    // it read as rendered rather than photographed.
    //
    // It also pays for the reflection. Specular does not scale with albedo,
    // so everything the box-projected probe puts on this floor now has half
    // again the contrast against it that it had.
    floor: 0xb8bcc1,
    envIntensity: 0.35,
  },
  {
    id: 'blue',
    labelKey: 'menu.LightBlue',
    noteKey: 'menu.LightBlueNote',
    background: 0x0a2e52,
    fog: 0x0a2e52,
    hemiSky: 0x6aa6ec,
    hemiGround: 0x061830,
    hemiIntensity: 0.34,
    key: 0x9cc8ff,
    keyIntensity: 0.22,
    lamp: 0x4ea6ff,
    lampIntensity: 15,
    ffuEmissive: 0x5cb0ff,
    ffuIntensity: 0.4,
    panel: 0xa8d8ff,
    // Same tile, same 0.71 of the light it used to send back - see the bright
    // preset. The hue is this preset's business; the value is the floor's.
    floor: 0x7297b2,
    envIntensity: 0.22,
  },
  {
    id: 'yellow',
    labelKey: 'menu.LightYellow',
    noteKey: 'menu.LightYellowNote',
    background: 0xd9a23e,
    fog: 0xd9a23e,
    hemiSky: 0xffcf70,
    hemiGround: 0x7a5c22,
    hemiIntensity: 0.85,
    key: 0xffca66,
    keyIntensity: 0.45,
    lamp: 0xffbe4a,
    lampIntensity: 13,
    ffuEmissive: 0xffc860,
    ffuIntensity: 0.45,
    panel: 0xffd98c,
    floor: 0xbda36b,
    envIntensity: 0.25,
  },
]

const STORAGE_KEY = 'simfab.lighting'

function readStored(): LightingId {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v && LIGHTING_PRESETS.some((p) => p.id === v)) return v as LightingId
  } catch {
    // Storage blocked - the default is fine.
  }
  return 'bright'
}

let current: LightingId = readStored()
const listeners = new Set<() => void>()

export function currentLighting(): LightingId {
  return current
}

export function lightingPreset(): LightingPreset {
  return LIGHTING_PRESETS.find((p) => p.id === current)!
}

export function setLighting(id: LightingId): void {
  if (id === current) return
  current = id
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // Not remembered, still applied.
  }
  for (const fn of listeners) fn()
}

export function onLightingChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}
